import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import WidgetRenderer from "@/components/WidgetRenderer";
import { Globe, ExternalLink } from "lucide-react";

export default function PublicDashboard() {
  const { token } = useParams();
  const [dash, setDash] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/public/dashboards/${token}`).then(({ data }) => setDash(data)).catch(() => setErr("This public dashboard is unavailable or has been unpublished."));
  }, [token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
      <div className="card text-center max-w-md">
        <div className="text-lg font-medium text-slate-900 mb-2">Not available</div>
        <div className="text-sm text-slate-500">{err}</div>
      </div>
    </div>
  );
  if (!dash) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  const brand = dash.company_branding || {};
  const widgets = dash.rendered_widgets || [];

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="bg-white border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {brand.logo_url ? <img src={brand.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
              : <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm" style={{ background: brand.primary_color || "#2563EB" }}>{(brand.name || "P").slice(0,1)}</div>}
            <div>
              <div className="font-display font-semibold text-slate-900">{brand.name || "PeopleIQ"}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Public dashboard</div>
            </div>
          </div>
          <a href="/" className="chip chip-primary" data-testid="powered-by">Powered by PeopleIQ <ExternalLink size={10} /></a>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 lg:p-10">
        <div className="mb-8">
          <div className="chip chip-success mb-3"><Globe size={12} /> Public</div>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-slate-900" data-testid="public-dashboard-title">{dash.title}</h1>
          {dash.description && <p className="text-slate-500 mt-3 max-w-2xl">{dash.description}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {widgets.length === 0 ? <div className="col-span-full card text-center text-slate-500">No widgets.</div>
            : widgets.map((w, i) => {
              const isBig = w.config?.type === "table";
              return <div key={w.id || i} className={isBig ? "md:col-span-2 xl:col-span-3" : ""}>
                <WidgetRenderer widget={w} height={240} />
              </div>;
            })}
        </div>
      </div>
    </div>
  );
}
